import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { OnlineBriefingInviteStyleModel } from '@api/briefings/model/briefing-invite-style.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ContentLibraryPanelFieldComponent } from '../../../../Common/PageBuilder/components/FormFields/ContentLibraryPanelField/content-library-panel.component';
import { ContentLibraryFieldModel } from '../../../../Common/PageBuilder/models/content-library-field-model';
import { TextFieldModel } from '../../../../Common/PageBuilder/models/text-field.model';
import { FILE_SIZE_LIMIT_5MiB } from '../../../../Core/constants';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ColorModel } from '@Common/Forms/models/color.model';

export enum EmailDesignStyleEnum {
    DEFAULT = 'DEFAULT',
    CUSTOM = 'CUSTOM'
}

@Component({
    selector: 'agpr-canvas-email-style',
    templateUrl: './canvas-email-style.component.html',
    styleUrls: ['./canvas-email-style.component.scss'],
    standalone: false,
})
export class CanvasEmailStyleComponent implements OnChanges{
    readonly designTypeEnum = EmailDesignStyleEnum;
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly contentLibrary = new ContentLibraryFieldModel(
        ContentLibraryPanelFieldComponent,
        'url',
        '',
        {
            maxFiles: 1,
            accepts: 'image/gif, image/jpeg, image/png',
            buttonLabel: 'Briefings.input.brandLogo.editImage',
            headerTooltip: 'Briefings.input.brandLogo.editImage',
            uploadButtonIcon: 'fa-regular fa-duotone fa-pen',
            defaultValue: undefined,
        }
    );
    readonly contentLibraryField = new TextFieldModel(
        null, null, null, null, null, null, null, null, null,
        (value: string) => {
            if (value?.length > FILE_SIZE_LIMIT_5MiB) {
                return this.localizationService.transform('Common.fileSize.limit.message', { size: '5MB' });
            }
            return null;
        }
    );

    @Input() id: string;
    @Input() onlineBriefingInviteStyle: OnlineBriefingInviteStyleModel;
    @Input() initialDesignType: EmailDesignStyleEnum;
    @Input() brandStyle: OnlineBriefingInviteStyleModel | null;
    @Input() brandId?: number;
    @Input() briefingId: number;
    @Output() onlineBriefingInviteStyleChange = new EventEmitter<OnlineBriefingInviteStyleModel>();

    isUserEdited: boolean;
    style: OnlineBriefingInviteStyleModel | null;
    designType: EmailDesignStyleEnum;
    brandTextColor: string;

    isEditingName: boolean = false;
    nameInputValue: string = '';
    placeholderText: string = '';
    lastBriefingId: number;
    logoLoadError: boolean = false;
    savedOnlineBriefingInviteStyle: OnlineBriefingInviteStyleModel | null = null;
    constructor(
        private localizationService: LocalizationService
    ){}

    ngOnChanges(changes: SimpleChanges): void {

        if (changes.briefingId && this.briefingId !== this.lastBriefingId) {
            this.lastBriefingId = this.briefingId;
            this.savedOnlineBriefingInviteStyle = this.onlineBriefingInviteStyle
                ? { ...this.onlineBriefingInviteStyle }
                : null;
            this.designType = this.initialDesignType;

            this.style = this.designType === EmailDesignStyleEnum.CUSTOM
                ? { ...this.onlineBriefingInviteStyle }
                : null;
            this.logoLoadError = false;

            this.isUserEdited = !!this.savedOnlineBriefingInviteStyle;
            this.updatePlaceholderText();
            this.updateBrandTextColor();
            return;
        }

        if (changes.onlineBriefingInviteStyle && !changes.onlineBriefingInviteStyle.firstChange) {
            const newSavedStyle = this.onlineBriefingInviteStyle;
            if (newSavedStyle && JSON.stringify(newSavedStyle) !== JSON.stringify(this.savedOnlineBriefingInviteStyle)) {
                this.savedOnlineBriefingInviteStyle = { ...newSavedStyle };
            }
        }

        if (changes.brandStyle) {
            if (this.designType === EmailDesignStyleEnum.CUSTOM && !this.isUserEdited && this.brandStyle) {
                this.style = { ...this.brandStyle };
                this.logoLoadError = false;
                this.updatePlaceholderText();
                this.updateBrandTextColor();
                this.emitChange();
            }
        }

    }

    updateLogoImage(url: string): void {
        this.brandLogoUrlChanged(url);
    }

    brandColorChanged(value: string): void {
        this.style = { ...this.style, brandColor: value };
        this.updateBrandTextColor();
        this.isUserEdited = true;
        this.emitChange();
    }

    brandNameChanged(value: string): void {
        this.style = { ...this.style!, brandName: value };
        this.isUserEdited = true;
        this.emitChange();
    }

    brandLogoUrlChanged(value: string): void {
        this.style = { ...this.style, brandLogoUrl: value };
        this.logoLoadError = false;
        this.isUserEdited = true;
        this.emitChange();
    }

    designTypeChanged(): void {
        if (this.designType === EmailDesignStyleEnum.DEFAULT) {
            this.style = null;
            this.isUserEdited = false;
        } else {
            if (this.savedOnlineBriefingInviteStyle) {
                this.style = { ...this.savedOnlineBriefingInviteStyle };
                this.isUserEdited = true;
            } else if (this.brandStyle) {
                this.style = { ...this.brandStyle };
                this.isUserEdited = false;
            } else {
                this.style = { brandColor: '#0D74BA', brandLogoUrl: '', brandName: 'Brand Name' };
                this.isUserEdited = false;
            }
        }
        this.logoLoadError = false;
        this.updateBrandTextColor();
        this.updateBrandTextColor();
        this.updatePlaceholderText();
        this.emitChange();
    }

    private emitChange(): void {
        this.onlineBriefingInviteStyleChange.emit(this.style);
    }

    private updateBrandTextColor(): void {
        this.brandTextColor = this.getContrastTextColor(this.style?.brandColor ?? '#FFFFFF');
    }

    doneEditingName(): void {
        if (!this.nameInputValue?.trim() || this.nameInputValue.trim() === this.style.brandName) {
            this.isEditingName = false;
            return;
        }

        this.style = { ...this.style, brandName: this.nameInputValue.trim() };
        this.isUserEdited = true;
        this.updatePlaceholderText();
        this.isEditingName = false;
        this.emitChange();
    }

    getContrastTextColor(hexColor: string): string {
        if (!hexColor) {
            return '#fff';
        }
        const color = new ColorModel(hexColor, null, null);
        const relativeLuminance = color.getRelativeLuminance();
        return relativeLuminance < 0.5 ? '#fff' : '#000';
    }

    onLogoLoadError(): void {
        this.logoLoadError = true;
        this.style = { ...this.style, brandLogoUrl: '' };
        this.emitChange();
    }

    private updatePlaceholderText(): void {
        const name = this.style?.brandName?.trim() ?? '';
        if (!name.length) {
            this.placeholderText = '';
            return;
        }
        const nameSplit = name.split(' ');
        this.placeholderText = nameSplit.length === 1
            ? name.slice(0, Math.min(nameSplit[0].length, 2)).toLocaleUpperCase()
            : (nameSplit[0][0] + nameSplit[nameSplit.length - 1][0]).toLocaleUpperCase();
    }


}